'use client';

import { BlockRoot, PortalLink, tx } from '@akademi/site-builder-runtime';
import { ActionLink, AddButton, LivePrice, type LinkAction } from '../home/parts';
import { useAddLines, useLiveLines, type LiveLine } from '../home/useLiveProducts';
import { TradeStyles } from '../styles/TradeStyles';
import { HOME_CSS } from '../styles/homeCss';

type Localized = string | Record<string, string>;

export interface SolarKit {
  /** The kit's short name, e.g. "Starter · 1 phase". */
  name: Localized;
  /** Its headline, e.g. "4.14 kWp" — the lines' own figures, never a rounded marketing size. */
  size?: Localized;
  /** What the kit is, line by line, as the shopper reads it. Prices come from `lines`. */
  contents?: Localized[];
  /** The catalogue products and quantities that make the kit, read live. */
  lines: LiveLine[];
  /** Words after the total's basis, e.g. "panels + inverter". */
  priceNote?: Localized;
  addLabel?: Localized;
  link?: LinkAction;
}

export interface SolarKitCardsProps {
  title?: Localized;
  seeAllLabel?: Localized;
  seeAllHref?: string;
  kits?: SolarKit[];
}

/**
 * Solar kits: panels and an inverter sold as one order, the easiest thing on the shop for a
 * member of the public to buy and the biggest lever on basket size.
 *
 * <p>A kit is a set of real catalogue lines. Its total is their live prices summed, so it is
 * never a number that has drifted from what the basket will charge, and there is no kit
 * discount unless the catalogue carries one. "Add kit" puts every line in the basket, where
 * the shopper can change any of them.
 */
export function SolarKitCards({ title, seeAllLabel, seeAllHref, kits = [] }: SolarKitCardsProps) {
  const shown = kits.filter((kit) => kit.lines?.length);
  if (!shown.length) return null;
  return (
    <BlockRoot as="section" className="ae ae-wrap ae-sec">
      <TradeStyles id="home" css={HOME_CSS} />
      {tx(title) ? (
        <div className="ae-sec__head">
          <h2 className="ae-sec__title">{tx(title)}</h2>
          {seeAllHref && tx(seeAllLabel) ? (
            <PortalLink href={seeAllHref} className="ae-sec__more">
              {tx(seeAllLabel)} →
            </PortalLink>
          ) : null}
        </div>
      ) : null}
      <div className="ae-kits">
        {shown.map((kit, index) => (
          <KitCard key={index} kit={kit} />
        ))}
      </div>
    </BlockRoot>
  );
}

function KitCard({ kit }: { kit: SolarKit }) {
  const live = useLiveLines(kit.lines);
  const adder = useAddLines(kit.lines, live.products);
  const outOfStock = live.products.some((product) => product?.availability === 'OUT_OF_STOCK');
  const contents = (kit.contents ?? []).map((line) => tx(line)).filter(Boolean);

  return (
    <article className="ae-kit">
      <span className="ae-kit__name">{tx(kit.name)}</span>
      {tx(kit.size) ? <span className="ae-kit__size">{tx(kit.size)}</span> : null}
      {contents.length ? (
        <ul className="ae-kit__lines">
          {contents.map((line, index) => (
            <li key={index}>{line}</li>
          ))}
        </ul>
      ) : null}
      <div className="ae-kit__foot">
        <LivePrice loading={live.loading} total={live.total} note={kit.priceNote} />
        <div className="ae-actions">
          {adder.available && tx(kit.addLabel) ? (
            <AddButton
              label={kit.addLabel!}
              state={adder.state}
              disabled={!live.buyable || adder.busy}
              outOfStock={outOfStock}
              onAdd={adder.add}
            />
          ) : null}
          <ActionLink action={kit.link} tone="ghost" />
        </div>
      </div>
    </article>
  );
}
